import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, Select } from 'syngenta-digital-cropwise-react-ui-kit';
import Button, {
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit/dist/components/button';
import { paths } from 'routes/paths';
import { useAuth } from 'utilities/base-auth';
import usePreferences, { PreferenceKey } from 'hooks/usePreferences';
import useWorkOrders from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useWorkOrders';
import workOrderNotification from 'screens/Applications&WorkOrders/WorkOrders/Components/Notification';
import focusInput from 'screens/Applications&WorkOrders/StepsLayout/Helpers/focusInput';
import ChevronUp from 'assets/chevron_up.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import './styles.css';

type ConversionAction = 'keep' | 'delete';

interface KeepOrDeleteWOModalProps {
  workOrderId: string;
  onClose: () => void;
  onError: () => void;
}

const KeepOrDeleteWOModal: React.FC<KeepOrDeleteWOModalProps> = ({
  workOrderId,
  onClose,
  onError
}) => {
  const history = useHistory();
  const { user } = useAuth();
  const { upsertPreference, findPreferenceByKey } = usePreferences();
  const { deleteWorkOrder } = useWorkOrders();

  const [selection, setSelection] = useState<ConversionAction>('keep');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const focusSubmitButton = () => {
    focusInput(modalContentRef, '[data-testid="keep-or-delete-wo-submit"]');
  };

  useEffect(() => {
    focusSubmitButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    findPreferenceByKey(PreferenceKey.WORK_ORDER_CONVERSION_ACTION, {
      userId: user.id
    })
      .then(preference => {
        const saved = preference?.preferenceValue?.action as
          | ConversionAction
          | undefined;
        if (saved === 'keep' || saved === 'delete') {
          setSelection(saved);
        }
      })
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDismiss = () => {
    if (isDeleting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const userId = user?.id;
    if (selection === 'keep') {
      if (userId) {
        upsertPreference({
          preferenceKey: PreferenceKey.WORK_ORDER_CONVERSION_ACTION,
          preferenceValue: { action: 'keep' },
          userId
        }).catch(() => {});
      }
      onClose();
      return;
    }

    setIsDeleting(true);
    try {
      await deleteWorkOrder(workOrderId);
      if (userId) {
        upsertPreference({
          preferenceKey: PreferenceKey.WORK_ORDER_CONVERSION_ACTION,
          preferenceValue: { action: 'delete' },
          userId
        }).catch(() => {});
      }
      workOrderNotification('DELETE_SUCCESS', () => {});
      onClose();
    } catch {
      setIsDeleting(false);
      onError();
      workOrderNotification('CONVERSION_DELETE_ERROR', () => {}, {
        onViewWorkOrder: () =>
          history.push({
            pathname: paths.workOrders,
            state: { operationId: workOrderId }
          })
      });
    }
  };

  return (
    <Modal
      open
      onCancel={handleDismiss}
      closable
      footer={null}
      width={423}
      centered
      className="keep-or-delete-wo-modal"
    >
      <div
        className="flex flex-col"
        style={{ gap: '40px' }}
        ref={modalContentRef}
      >
        <div className="flex flex-col" style={{ gap: '16px' }}>
          <div
            className="text-base font-semibold"
            style={{ lineHeight: '24px' }}
          >
            What would you like to do with the original Work Order?
          </div>
          <Select
            className="custom-select w-full"
            value={selection}
            onChange={(value: ConversionAction) => setSelection(value)}
            onDropdownVisibleChange={setIsSelectOpen}
            showArrow
            suffixIcon={
              isSelectOpen ? (
                <ChevronUp width={18} height={18} />
              ) : (
                <ChevronDown width={18} height={18} />
              )
            }
          >
            <Select.Option value="keep">Keep Work Order</Select.Option>
            <Select.Option value="delete">Delete Work Order</Select.Option>
          </Select>
        </div>
        <div className="flex justify-between">
          <Button
            type={ButtonType.outline}
            onClick={handleDismiss}
            disabled={isDeleting}
          >
            Dismiss
          </Button>
          <Button
            data-testid="keep-or-delete-wo-submit"
            type={ButtonType.primary}
            color="green"
            className="bg-green-60"
            style={{ backgroundColor: '#14803C', borderColor: '#14803C' }}
            disabled={isDeleting}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default KeepOrDeleteWOModal;
